import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import CodeMirror from "@uiw/react-codemirror";
import { html as htmlLang } from "@codemirror/lang-html";
import { EditorView } from "@codemirror/view";
import {
  DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_HTML,
  DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_SUBJECT,
} from "../../lib/checkoutConfirmationEmailDefaults";

interface TippingData {
  enabled: boolean;
  defaultSelected?: boolean;
  amount1?: number;
  amount2?: number;
  amount3?: number;
  title?: string;
  description?: string;
}

interface ColorsData {
  backgroundColor?: string;
  buttonColor?: string;
  accentColor?: string;
}

interface CatalogProductLite {
  _id: string;
  name?: string;
}

interface ShippingZoneFormRow {
  id: string;
  zoneName: string;
  countriesCsv: string;
  optionName: string;
  priceDollars: string;
}

interface ShippingProfileFormRow {
  id: string;
  profileName: string;
  selectedProductIds: string[];
  zones: ShippingZoneFormRow[];
}

interface ShippingProtectionData {
  enabled: boolean;
  priceDollars: string;
}

interface CheckoutSettingsData {
  tipping: TippingData;
  colors: ColorsData;
  shippingProfileRows: ShippingProfileFormRow[];
  shippingProtection: ShippingProtectionData;
  metaPixelId: string;
  confirmationEmailSubject: string;
  confirmationEmailHtml: string;
  receiptFooterHtml: string;
}

function newZoneRow(): ShippingZoneFormRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    zoneName: "",
    countriesCsv: "",
    optionName: "Standard",
    priceDollars: "",
  };
}

function newProfileRow(): ShippingProfileFormRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    profileName: "",
    selectedProductIds: [],
    zones: [newZoneRow()],
  };
}

function zonesFromApiProfile(p: {
  shippingZones?: Array<{
    name: string;
    countries?: string[];
    prices?: Array<{ name: string; price: number }>;
  }>;
}): ShippingZoneFormRow[] {
  if (Array.isArray(p.shippingZones) && p.shippingZones.length > 0) {
    return p.shippingZones.map((z, zi) => ({
      id: `z-${zi}-${z.name}-${Math.random().toString(36).slice(2)}`,
      zoneName: z.name ?? "",
      countriesCsv: (z.countries ?? []).filter(Boolean).join(", "),
      optionName: z.prices?.[0]?.name ?? "Standard",
      priceDollars:
        z.prices?.[0] != null && Number.isFinite(z.prices[0].price)
          ? (z.prices[0].price / 100).toFixed(2)
          : "",
    }));
  }
  return [newZoneRow()];
}

function shippingProfilesFromApi(
  raw:
    | Array<{
        name: string;
        productIds?: string[];
        shippingZones?: Array<{
          name: string;
          countries?: string[];
          prices?: Array<{ name: string; price: number }>;
        }>;
      }>
    | undefined
): ShippingProfileFormRow[] {
  if (!raw?.length) return [];
  return raw.map((p, i) => ({
    id: `pf-${i}-${p.name}`,
    profileName: p.name ?? "",
    selectedProductIds: (p.productIds ?? []).map((id) => String(id)),
    zones: zonesFromApiProfile(p),
  }));
}

function formRowsToShippingProfiles(rows: ShippingProfileFormRow[]) {
  return rows
    .filter((r) => r.profileName.trim() !== "")
    .map((r) => ({
      name: r.profileName.trim(),
      productIds: r.selectedProductIds,
      shippingZones: r.zones.map((z) => {
        const parsed = parseFloat(z.priceDollars);
        const priceCents =
          !Number.isNaN(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0;
        const countries = z.countriesCsv
          .split(/[\s,]+/)
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean);
        return {
          name: z.zoneName.trim() || "Zone",
          countries,
          prices: [
            {
              name: (z.optionName.trim() || "Standard").trim(),
              price: priceCents,
            },
          ],
        };
      }),
    }));
}

export function SettingsCheckout() {
  const [catalogProducts, setCatalogProducts] = useState<CatalogProductLite[]>([]);
  const [checkoutSettings, setCheckoutSettings] = useState<CheckoutSettingsData>({
    tipping: {
      enabled: false,
      amount1: undefined,
      amount2: undefined,
      amount3: undefined,
    },
    colors: {},
    shippingProfileRows: [],
    shippingProtection: { enabled: false, priceDollars: "" },
    metaPixelId: "",
    confirmationEmailSubject: DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_SUBJECT,
    confirmationEmailHtml: DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_HTML,
    receiptFooterHtml: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const [checkoutRes, productsRes] = await Promise.all([
          fetch("/api/checkout-settings"),
          fetch("/api/products"),
        ]);

        if (productsRes.ok) {
          const pj = await productsRes.json().catch(() => ({}));
          const loaded = (pj.products as CatalogProductLite[] | undefined) ?? [];
          setCatalogProducts(loaded);
        }

        if (checkoutRes.ok) {
          const data = await checkoutRes.json();
          if (data.checkoutSettings) {
            setCheckoutSettings({
              tipping: {
                enabled: data.checkoutSettings.tipping?.enabled || false,
                defaultSelected: data.checkoutSettings.tipping?.defaultSelected || false,
                amount1: data.checkoutSettings.tipping?.amount1,
                amount2: data.checkoutSettings.tipping?.amount2,
                amount3: data.checkoutSettings.tipping?.amount3,
                title: data.checkoutSettings.tipping?.title,
                description: data.checkoutSettings.tipping?.description,
              },
              colors: data.checkoutSettings.colors || {},
              shippingProfileRows: shippingProfilesFromApi(
                data.checkoutSettings.shippingProfiles
              ),
              shippingProtection: {
                enabled: data.checkoutSettings.shippingProtection?.enabled ?? false,
                priceDollars:
                  data.checkoutSettings.shippingProtection?.price != null &&
                  Number.isFinite(data.checkoutSettings.shippingProtection.price)
                    ? (data.checkoutSettings.shippingProtection.price / 100).toFixed(2)
                    : "",
              },
              metaPixelId: data.checkoutSettings.metaPixelId ?? "",
              confirmationEmailSubject:
                data.checkoutSettings.confirmationEmail?.subject?.trim() ||
                DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_SUBJECT,
              confirmationEmailHtml:
                data.checkoutSettings.confirmationEmail?.html?.trim() ||
                DEFAULT_CHECKOUT_CONFIRMATION_EMAIL_HTML,
              receiptFooterHtml: data.checkoutSettings.receiptFooterHtml ?? "",
            });
          }
        } else {
          console.error("Failed to fetch checkout settings");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch("/api/checkout-settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tipping: checkoutSettings.tipping,
          colors: checkoutSettings.colors,
          shippingProfiles: formRowsToShippingProfiles(checkoutSettings.shippingProfileRows),
          shippingProtection: {
            enabled: checkoutSettings.shippingProtection.enabled,
            price: (() => {
              const parsed = parseFloat(checkoutSettings.shippingProtection.priceDollars);
              return !Number.isNaN(parsed) && parsed >= 0 ? Math.round(parsed * 100) : 0;
            })(),
          },
          metaPixelId: checkoutSettings.metaPixelId.trim(),
          confirmationEmail: {
            subject: checkoutSettings.confirmationEmailSubject,
            html: checkoutSettings.confirmationEmailHtml,
          },
          receiptFooterHtml: checkoutSettings.receiptFooterHtml.trim(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save checkout settings");
      }

      toast.success("Checkout settings saved successfully");
    } catch (error) {
      console.error("Error saving checkout settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save checkout settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmationEmailHtmlChange = useCallback((value: string) => {
    setCheckoutSettings((prev) => ({
      ...prev,
      confirmationEmailHtml: value,
    }));
  }, []);

  const handleReceiptFooterHtmlChange = useCallback((value: string) => {
    setCheckoutSettings((prev) => ({
      ...prev,
      receiptFooterHtml: value,
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tipping</CardTitle>
          <CardAction>
            <Switch
              checked={checkoutSettings.tipping.enabled}
              onCheckedChange={(checked) => {
                setCheckoutSettings({
                  ...checkoutSettings,
                  tipping: {
                    ...checkoutSettings.tipping,
                    enabled: checked,
                  },
                });
              }}
            />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-4">
          {checkoutSettings.tipping.enabled && (
            <div className="space-y-4 pl-6 border-l-2 border-muted">
              <div className="flex items-center space-x-2 pb-2">
                <Checkbox
                  id="defaultSelected"
                  checked={checkoutSettings.tipping.defaultSelected || false}
                  onCheckedChange={(checked) => {
                    setCheckoutSettings({
                      ...checkoutSettings,
                      tipping: {
                        ...checkoutSettings.tipping,
                        defaultSelected: checked === true,
                      },
                    });
                  }}
                />
                <Label htmlFor="defaultSelected" className="text-sm font-medium leading-none cursor-pointer">
                  Check tip box by default at checkout
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount1">Tip Amount 1 ($)</Label>
                <Input
                  id="amount1"
                  type="number"
                  step="0.01"
                  min="0"
                  value={checkoutSettings.tipping.amount1 !== undefined ? checkoutSettings.tipping.amount1 : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || value === ".") {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount1: undefined,
                        },
                      });
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount1: numValue,
                        },
                      });
                    }
                  }}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount2">Tip Amount 2 ($)</Label>
                <Input
                  id="amount2"
                  type="number"
                  step="0.01"
                  min="0"
                  value={checkoutSettings.tipping.amount2 !== undefined ? checkoutSettings.tipping.amount2 : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || value === ".") {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount2: undefined,
                        },
                      });
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount2: numValue,
                        },
                      });
                    }
                  }}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount3">Tip Amount 3 ($)</Label>
                <Input
                  id="amount3"
                  type="number"
                  step="0.01"
                  min="0"
                  value={checkoutSettings.tipping.amount3 !== undefined ? checkoutSettings.tipping.amount3 : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "" || value === ".") {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount3: undefined,
                        },
                      });
                      return;
                    }
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setCheckoutSettings({
                        ...checkoutSettings,
                        tipping: {
                          ...checkoutSettings.tipping,
                          amount3: numValue,
                        },
                      });
                    }
                  }}
                  placeholder="Enter amount"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipTitle">Default checkbox label</Label>
                <Input
                  id="tipTitle"
                  value={checkoutSettings.tipping.title ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCheckoutSettings({
                      ...checkoutSettings,
                      tipping: {
                        ...checkoutSettings.tipping,
                        title: value.trim() !== "" ? value : undefined,
                      },
                    });
                  }}
                  placeholder="Add tip"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipDescription">Default description (optional)</Label>
                <Textarea
                  id="tipDescription"
                  value={checkoutSettings.tipping.description ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCheckoutSettings({
                      ...checkoutSettings,
                      tipping: {
                        ...checkoutSettings.tipping,
                        description: value.trim() !== "" ? value : undefined,
                      },
                    });
                  }}
                  placeholder="Shown when the customer opts in to add a tip"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">Supports markdown.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Shipping profiles</CardTitle>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setCheckoutSettings((prev) => ({
                ...prev,
                shippingProfileRows: [...prev.shippingProfileRows, newProfileRow()],
              }))
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add profile
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Group physical products that ship in the same package. Each profile contributes one shipping charge at
            checkout regardless of how many grouped products are in the cart. Match each physical product to exactly
            one profile so shipping is quoted correctly. Within each profile, add zones by country; checkout picks the
            zone whose country list includes the buyer&apos;s country, or the zone with no countries (default).
          </p>
          {checkoutSettings.shippingProfileRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No profiles — add one to charge shipping based on what is in the cart.
            </p>
          ) : null}
          {checkoutSettings.shippingProfileRows.map((row, index) => (
            <div key={row.id} className="space-y-3 p-4 border rounded-md relative">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 text-muted-foreground"
                onClick={() =>
                  setCheckoutSettings((prev) => ({
                    ...prev,
                    shippingProfileRows: prev.shippingProfileRows.filter((_, i) => i !== index),
                  }))
                }
                aria-label="Remove profile"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <div className="space-y-2 pr-10">
                <Label>Profile name</Label>
                <Input
                  value={row.profileName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCheckoutSettings((prev) => {
                      const next = [...prev.shippingProfileRows];
                      next[index] = { ...next[index], profileName: v };
                      return { ...prev, shippingProfileRows: next };
                    });
                  }}
                  placeholder="e.g. Vinyl box"
                />
              </div>
              <div className="space-y-2">
                <Label>Products in this shipment</Label>
                {catalogProducts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No products found — create products first.</p>
                ) : (
                  <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                    {catalogProducts.map((prod) => {
                      const checked = row.selectedProductIds.includes(prod._id);
                      return (
                        <label
                          key={prod._id}
                          className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(c) => {
                              const on = c === true;
                              setCheckoutSettings((prev) => {
                                const next = [...prev.shippingProfileRows];
                                const cur = new Set(next[index].selectedProductIds);
                                if (on) cur.add(prod._id);
                                else cur.delete(prod._id);
                                next[index] = {
                                  ...next[index],
                                  selectedProductIds: [...cur],
                                };
                                return { ...prev, shippingProfileRows: next };
                              });
                            }}
                          />
                          <span>{prod.name?.trim() || "Untitled product"}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-3 pt-2 border-t">
                <div className="flex flex-row items-center justify-between gap-2 flex-wrap">
                  <Label className="text-base font-medium">Shipping zones</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCheckoutSettings((prev) => {
                        const next = [...prev.shippingProfileRows];
                        const cur = next[index];
                        next[index] = {
                          ...cur,
                          zones: [...cur.zones, newZoneRow()],
                        };
                        return { ...prev, shippingProfileRows: next };
                      })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add zone
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Country codes (ISO 3166-1 alpha-2), comma-separated. Leave countries empty for a default rate when no
                  listed country matches the buyer&apos;s address.
                </p>
                {row.zones.map((zone, zIndex) => (
                  <div
                    key={zone.id}
                    className="space-y-3 p-3 rounded-md bg-muted/30 border border-muted relative"
                  >
                    {row.zones.length > 1 ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute top-1.5 right-1.5 h-8 w-8 text-muted-foreground"
                        onClick={() =>
                          setCheckoutSettings((prev) => {
                            const next = [...prev.shippingProfileRows];
                            const nextZones = next[index].zones.filter((_, zi) => zi !== zIndex);
                            next[index] = {
                              ...next[index],
                              zones: nextZones.length > 0 ? nextZones : [newZoneRow()],
                            };
                            return { ...prev, shippingProfileRows: next };
                          })
                        }
                        aria-label="Remove zone"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                    <div className="space-y-2 pr-8">
                      <Label>Zone name</Label>
                      <Input
                        value={zone.zoneName}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCheckoutSettings((prev) => {
                            const next = [...prev.shippingProfileRows];
                            const zones = [...next[index].zones];
                            zones[zIndex] = { ...zones[zIndex], zoneName: v };
                            next[index] = { ...next[index], zones };
                            return { ...prev, shippingProfileRows: next };
                          });
                        }}
                        placeholder="e.g. Domestic"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Countries</Label>
                      <Input
                        value={zone.countriesCsv}
                        onChange={(e) => {
                          const v = e.target.value;
                          setCheckoutSettings((prev) => {
                            const next = [...prev.shippingProfileRows];
                            const zones = [...next[index].zones];
                            zones[zIndex] = { ...zones[zIndex], countriesCsv: v };
                            next[index] = { ...next[index], zones };
                            return { ...prev, shippingProfileRows: next };
                          });
                        }}
                        placeholder="US, CA or leave empty for default"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Shipping option name</Label>
                        <Input
                          value={zone.optionName}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCheckoutSettings((prev) => {
                              const next = [...prev.shippingProfileRows];
                              const zones = [...next[index].zones];
                              zones[zIndex] = { ...zones[zIndex], optionName: v };
                              next[index] = { ...next[index], zones };
                              return { ...prev, shippingProfileRows: next };
                            });
                          }}
                          placeholder="Standard"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Price (USD)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={zone.priceDollars}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCheckoutSettings((prev) => {
                              const next = [...prev.shippingProfileRows];
                              const zones = [...next[index].zones];
                              zones[zIndex] = { ...zones[zIndex], priceDollars: v };
                              next[index] = { ...next[index], zones };
                              return { ...prev, shippingProfileRows: next };
                            });
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Shipping protection</CardTitle>
          <CardAction>
            <Switch
              checked={checkoutSettings.shippingProtection.enabled}
              onCheckedChange={(checked) => {
                setCheckoutSettings({
                  ...checkoutSettings,
                  shippingProtection: {
                    ...checkoutSettings.shippingProtection,
                    enabled: checked,
                  },
                });
              }}
            />
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Optional add-on for physical checkouts. Buyers see a pre-checked &quot;Shipping Protection&quot; line after
            shipping; price is in addition to shipping.
          </p>
          {checkoutSettings.shippingProtection.enabled ? (
            <div className="space-y-2 pl-6 border-l-2 border-muted max-w-sm">
              <Label htmlFor="shipping-protection-price">Price (USD)</Label>
              <Input
                id="shipping-protection-price"
                type="number"
                step="0.01"
                min="0"
                value={checkoutSettings.shippingProtection.priceDollars}
                onChange={(e) => {
                  const v = e.target.value;
                  setCheckoutSettings({
                    ...checkoutSettings,
                    shippingProtection: {
                      ...checkoutSettings.shippingProtection,
                      priceDollars: v,
                    },
                  });
                }}
                placeholder="0.00"
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Meta Pixel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Facebook / Meta Pixel ID (numeric). Used for standard checkout events (InitiateCheckout,
            AddShippingInfo, Purchase) on hosted checkout pages.
          </p>
          <div className="space-y-2 max-w-md">
            <Label htmlFor="metaPixelId">Pixel ID</Label>
            <Input
              id="metaPixelId"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="e.g. 123456789012345"
              value={checkoutSettings.metaPixelId}
              onChange={(e) =>
                setCheckoutSettings({
                  ...checkoutSettings,
                  metaPixelId: e.target.value,
                })
              }
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Order confirmation email</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sent automatically when a checkout completes and the checkout has no success actions.
            Supports Handlebars: <code className="text-xs">{"{{firstName}}"}</code>,{" "}
            <code className="text-xs">{"{{artist.name}}"}</code>,{" "}
            <code className="text-xs">{"{{orderUrl}}"}</code>,{" "}
            <code className="text-xs">{"{{{orderSummary}}}"}</code>,{" "}
            <code className="text-xs">{"{{{orderDetails}}}"}</code>.
          </p>
          <div className="space-y-2">
            <Label htmlFor="confirmationEmailSubject">Subject</Label>
            <Input
              id="confirmationEmailSubject"
              value={checkoutSettings.confirmationEmailSubject}
              onChange={(e) =>
                setCheckoutSettings({
                  ...checkoutSettings,
                  confirmationEmailSubject: e.target.value,
                })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmationEmailHtml">HTML</Label>
            <div className="border rounded-md overflow-hidden min-h-[240px]">
              <CodeMirror
                value={checkoutSettings.confirmationEmailHtml}
                height="240px"
                extensions={[htmlLang(), EditorView.lineWrapping]}
                onChange={handleConfirmationEmailHtmlChange}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  highlightActiveLine: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                }}
                className="text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Receipt footer HTML</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Custom HTML displayed at the bottom of the order receipt page.
          </p>
          <div className="space-y-2">
            <div className="border rounded-md overflow-hidden min-h-[240px]">
              <CodeMirror
                value={checkoutSettings.receiptFooterHtml}
                height="240px"
                extensions={[htmlLang(), EditorView.lineWrapping]}
                onChange={handleReceiptFooterHtmlChange}
                basicSetup={{
                  lineNumbers: true,
                  foldGutter: true,
                  highlightActiveLine: true,
                  bracketMatching: true,
                  closeBrackets: true,
                  autocompletion: true,
                }}
                className="text-sm"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="backgroundColor">Background color</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="backgroundColor"
                type="color"
                className="w-12 h-10 p-1 cursor-pointer"
                value={checkoutSettings.colors.backgroundColor || "#ffffff"}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      backgroundColor: e.target.value,
                    },
                  })
                }
              />
              <Input
                type="text"
                placeholder="#ffffff"
                value={checkoutSettings.colors.backgroundColor || ""}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      backgroundColor: e.target.value || undefined,
                    },
                  })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="buttonColor">Button color</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="buttonColor"
                type="color"
                className="w-12 h-10 p-1 cursor-pointer"
                value={checkoutSettings.colors.buttonColor || "#000000"}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      buttonColor: e.target.value,
                    },
                  })
                }
              />
              <Input
                type="text"
                placeholder="#000000"
                value={checkoutSettings.colors.buttonColor || ""}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      buttonColor: e.target.value || undefined,
                    },
                  })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="accentColor">Accent color</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="accentColor"
                type="color"
                className="w-12 h-10 p-1 cursor-pointer"
                value={checkoutSettings.colors.accentColor || "#6366f1"}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      accentColor: e.target.value,
                    },
                  })
                }
              />
              <Input
                type="text"
                placeholder="#6366f1"
                value={checkoutSettings.colors.accentColor || ""}
                onChange={(e) =>
                  setCheckoutSettings({
                    ...checkoutSettings,
                    colors: {
                      ...checkoutSettings.colors,
                      accentColor: e.target.value || undefined,
                    },
                  })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
